/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';

import { css, StyleSheet } from 'aphrodite';
import { ActionButton, AlertBar, Text } from '@blend/enterprise-components';
import { FeeFormValue, FormShape } from '../../types';
import { ReduxProps } from './MoreInfoRequestAlertContainer';
import { Pricing } from 'blb-types/pricing';
import { isNil, isEmpty } from 'lodash';
import { getMoreInfoRequestCopy } from '../../utils/ledgerUtils';

const styles = StyleSheet.create({
  moreInfoMessagesWrapper: {
    position: 'relative',
    width: '100%',
  },
  moreInfoMessageWrapper: {
    ':last-child': {
      marginBottom: 0,
    },
    alignItems: 'center',
    display: 'flex',
    flex: 1,
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  moreInfoRequestAlertWrapper: {
    marginBottom: 35,
  },
});

export interface MoreInfoRequestAlertProps {
  formValues: FormShape;
  loanId: string;
  setDrawerOpen: (isOpen: boolean) => void;
}

export type Props = MoreInfoRequestAlertProps & ReduxProps;
export const MoreInfoRequestAlert: React.FC<Props> = ({ setDrawerOpen, setSelectedThread, formValues, loanId }) => {
  const viewThread = (threadId: string) => {
    setDrawerOpen(true);
    setSelectedThread(threadId, loanId);
  };

  const getFeesWithMoreInfoRequested = () => {
    const feesWithMoreInfoRequested = [];
    for (const subsectionKey of Object.values(Pricing.LoanEstimateSectionTypes)) {
      const subsection = formValues[subsectionKey];
      if (isNil(subsection)) continue;
      for (const fee of subsection) {
        if (fee.moreInfoRequested) {
          feesWithMoreInfoRequested.push(fee);
        }
      }
    }

    return feesWithMoreInfoRequested;
  };

  const renderMoreInfoMessages = (feesWithMoreInfoRequested: FeeFormValue[]) => {
    const renderedFees = feesWithMoreInfoRequested.map(fee => {
      const { sharedInboxThreadId } = fee;
      return (
        <div className={css(styles.moreInfoMessageWrapper)} key={`alert-message-${fee.id}`}>
          <Text>{`Need more info: ${fee.name}, ${getMoreInfoRequestCopy(fee)}`}</Text>
          {sharedInboxThreadId && (
            <ActionButton
              btnSize="sm"
              icon="message"
              label="View Message"
              onClick={() => viewThread(sharedInboxThreadId)}
              theme="tertiary"
              type="button"
            />
          )}
        </div>
      );
    });

    return <div className={css(styles.moreInfoMessagesWrapper)}>{renderedFees}</div>;
  };

  const feesWithMoreInfoRequested = getFeesWithMoreInfoRequested();
  return !isEmpty(feesWithMoreInfoRequested) ? (
    <div className={css(styles.moreInfoRequestAlertWrapper)}>
      <AlertBar
        id="it-MoreInfoRequested-Alert"
        message={renderMoreInfoMessages(feesWithMoreInfoRequested)}
        size="md"
        theme="warn"
      />
    </div>
  ) : null;
};
